/**
 * /venues/:venueSlug/admin/charity — what each season played for, and what it
 * raised.
 *
 * ONE CARD PER SEASON, newest first. The history IS the list: season_charity
 * holds one row per season by constraint, so a page that made you click into a
 * season to see its charity would be hiding the only view anybody wants — the
 * run of years with a number against each.
 *
 * A SEASON WITH NO ROW LOOKS LIKE AN EMPTY CARD, not a missing one. The
 * endpoint answers with a null-filled record rather than a 404 for exactly this
 * reason, so every season is editable whether or not anything has been written
 * against it yet. A row appears on the first save.
 *
 * ONLY WHEN THE VENUE RUNS A CHARITY. charity_enabled gates the page and its
 * nav entry — a venue with no charity angle should not carry a page about one.
 * The guard here is a courtesy, not a security boundary: the endpoints are
 * venue_admin regardless, and nothing here is secret from an admin who types
 * the URL. It is about not showing somebody a feature they have switched off.
 *
 * BLANK AND ZERO ARE DIFFERENT, all the way down to the column. A season that
 * has not counted its money yet has not raised zero, and the two must not read
 * the same — see the nullable REAL in the schema.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPut, apiDel, apiUpload } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import { formatDate } from '../lib/format.js';

/** A total, the way it would be read out. No pence unless there are pence. */
function money(amount) {
  if (amount === null || amount === undefined) return null;
  const pence = Number(amount) % 1 !== 0;
  return `£${new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: pence ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(Number(amount))}`;
}

/* ------------------------------------------------------------------------- */

/**
 * One season's charity record.
 *
 * Its own component with its own form state, because a page of eight seasons
 * editing into one shared object would make every keystroke re-render all of
 * them — and, worse, would make "which card is dirty" a question rather than a
 * fact each card already knows about itself.
 */
function CharityCard({ venueId, season }) {
  const [record, setRecord] = useState(null);
  const [form, setForm]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]   = useState(null);
  const [saved, setSaved]   = useState(false);

  const load = useCallback(async () => {
    const { data, error: err } = await apiGet(
      `/api/venues/${venueId}/seasons/${season.id}/charity`
    );

    if (err) { setError(err); return; }

    const charity = data?.charity ?? null;
    setRecord(charity);
    setForm({
      charityName: charity?.charityName ?? '',
      description: charity?.description ?? '',
      nominatedBy: charity?.nominatedBy ?? '',
      // '' rather than 0 for an unset total, so the box is empty and clearing
      // it means "not counted yet" rather than "raised nothing". == null
      // catches both null and undefined while leaving a genuine 0 alone.
      totalRaised: charity?.totalRaised == null ? '' : String(charity.totalRaised),
    });
  }, [venueId, season.id]);

  useEffect(() => { load(); }, [load]);

  if (!form) {
    return (
      <section className="hd-card hd-stack">
        <h2 className="hd-section-title" style={{ margin: 0 }}>{season.name}</h2>
        <p className="hd-muted">{error ?? 'Loading…'}</p>
      </section>
    );
  }

  const setField = (key, value) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async () => {
    setBusy(true);
    setError(null);

    const { data, error: err } = await apiPut(
      `/api/venues/${venueId}/seasons/${season.id}/charity`,
      {
        charityName: form.charityName,
        description: form.description,
        nominatedBy: form.nominatedBy,
        // '' clears the figure; the endpoint treats absent, null and '' alike.
        totalRaised: form.totalRaised === '' ? null : form.totalRaised,
      }
    );

    setBusy(false);
    if (err) { setError(err); return; }

    setRecord(data?.charity ?? null);
    setSaved(true);
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    setError(null);

    const body = new FormData();
    body.set('file', file);

    const { data, error: err } = await apiUpload(
      `/api/venues/${venueId}/seasons/${season.id}/charity`,
      body
    );

    setUploading(false);
    if (err) { setError(err); return; }
    setRecord(data?.charity ?? null);
  };

  const removePhoto = async () => {
    const ok = window.confirm(
      'Remove this photo?\n\nIt comes out of storage. Putting it back means '
      + 'uploading it again.'
    );
    if (!ok) return;

    setError(null);
    const { data, error: err } = await apiDel(
      `/api/venues/${venueId}/seasons/${season.id}/charity`
    );

    if (err) { setError(err); return; }
    setRecord(data?.charity ?? null);
  };

  const total = money(record?.totalRaised);

  return (
    <section className="hd-card hd-stack">
      <div className="hd-row-between">
        <div>
          <h2 className="hd-section-title" style={{ margin: 0 }}>{season.name}</h2>
          <p className="hd-meta">
            {season.startDate ? formatDate(season.startDate) : 'No start date'}
            {season.endDate && <> – {formatDate(season.endDate)}</>}
            {' · '}{season.tournamentCount} night{season.tournamentCount === 1 ? '' : 's'}
          </p>
        </div>

        {/* The headline figure, from the SAVED record rather than the form —
            this is what the season raised, not what is currently typed. */}
        {total && <span className="hd-badge hd-badge-gold">{total}</span>}
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-field">
        <label className="hd-label" htmlFor={`charity-name-${season.id}`}>Charity</label>
        <input
          id={`charity-name-${season.id}`}
          className="hd-input"
          type="text"
          maxLength={120}
          value={form.charityName}
          disabled={busy}
          placeholder="Not set"
          onChange={(e) => setField('charityName', e.target.value)}
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor={`charity-desc-${season.id}`}>About</label>
        <textarea
          id={`charity-desc-${season.id}`}
          className="hd-textarea"
          rows={3}
          maxLength={2000}
          value={form.description}
          disabled={busy}
          placeholder="What they do, and why the room picked them."
          onChange={(e) => setField('description', e.target.value)}
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor={`charity-by-${season.id}`}>Nominated by</label>
        <input
          id={`charity-by-${season.id}`}
          className="hd-input"
          type="text"
          maxLength={120}
          value={form.nominatedBy}
          disabled={busy}
          placeholder="A name, not an account"
          onChange={(e) => setField('nominatedBy', e.target.value)}
        />
        <p className="hd-hint">
          Free text on purpose — usually a regular in the room rather than
          somebody with a login.
        </p>
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor={`charity-total-${season.id}`}>Total raised (£)</label>
        <input
          id={`charity-total-${season.id}`}
          className="hd-input"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={form.totalRaised}
          disabled={busy}
          placeholder="Not counted yet"
          onChange={(e) => setField('totalRaised', e.target.value)}
        />
        <p className="hd-hint">
          Leave empty until the money is counted. Empty and £0 are not the same
          thing here.
        </p>
      </div>

      <div className="hd-field">
        <span className="hd-label">Photo</span>

        {record?.photoUrl ? (
          <div className="hd-charity-photo">
            <img src={record.photoUrl} alt="" />
          </div>
        ) : (
          <p className="hd-muted">None.</p>
        )}

        <div className="hd-row">
          <label className="hd-btn hd-btn-secondary">
            {uploading ? 'Uploading…' : record?.photoUrl ? 'Replace photo' : 'Add photo'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first: choosing the same file twice fires no change
                // event otherwise, and the second attempt looks like a dead
                // button.
                e.target.value = '';
                if (file) uploadPhoto(file);
              }}
            />
          </label>

          {record?.photoUrl && (
            <button type="button" className="hd-btn hd-btn-quiet" onClick={removePhoto}>
              Remove photo
            </button>
          )}
        </div>
      </div>

      <div className="hd-row">
        <button
          type="button"
          className="hd-btn hd-btn-primary"
          disabled={busy}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>

        {saved && <span className="hd-hint">Saved.</span>}

        {/* The photo is already stored; only the text half is pending. */}
        {!saved && <span className="hd-hint">Photos upload immediately.</span>}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */

export default function AdminCharity() {
  const { venueSlug } = useParams();
  const contextVenue = useVenue(venueSlug);
  const contextId = contextVenue?.venue_id ?? null;

  const [venueId, setVenueId] = useState(contextId);
  const [state, setState]     = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      let id = contextId;

      if (id === null) {
        const { data, error } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
        if (cancelled) return;

        if (error || !data?.venue) {
          setState({ status: 'error', error: error ?? 'Venue not found' });
          return;
        }
        id = data.venue.id;
      }

      if (cancelled) return;
      setVenueId(id);

      const [settings, seasons] = await Promise.all([
        apiGet(`/api/venues/${id}/settings/public`),
        apiGet(`/api/venues/${id}/seasons`),
      ]);

      if (cancelled) return;

      if (seasons.error) {
        setState({ status: 'error', error: seasons.error });
        return;
      }

      setState({
        status:   'ready',
        enabled:  settings.data?.settings?.charity_enabled === '1',
        seasons:  seasons.data?.seasons ?? [],
      });
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId]);

  if (state.status === 'loading') return <div className="hd-empty">Loading…</div>;

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Charity</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  // Off at the venue. Said plainly, with where to turn it on — an admin who
  // reached this URL was looking for something, and a blank page would not
  // tell them why it is not here.
  if (!state.enabled) {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Charity</h1>
        <p className="hd-muted">
          This venue is not running a charity. Turn on “charity enabled” in
          venue settings and this page becomes available.
        </p>
      </div>
    );
  }

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">Charity</h1>
        <p className="hd-meta">
          {contextVenue?.name ?? venueSlug} · one record per season
        </p>
      </div>

      {state.seasons.length === 0 ? (
        <p className="hd-muted">
          No seasons yet. A charity record belongs to a season, so start one
          first.
        </p>
      ) : (
        state.seasons.map((season) => (
          <CharityCard key={season.id} venueId={venueId} season={season} />
        ))
      )}
    </div>
  );
}
